<svelte:options
  customElement={{
    tag: "oc-floating-focus-v1",
    /*                       */
    extend: window.__components.extend(),
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import { on } from "svelte/events";
  import { useInterval } from "@otto-ec/otto-components-utils/svelte/useInterval";
  import { repositionElement } from "./utils/floaterUtils";
  import { findNextPositionedParent } from "./utils/findNextPositionedParent";
  import { track } from "../../../common/utils/track";
  import { isActive } from "./utils/isActive";
  import { useActiveElement } from "./utils/useActiveElement.svelte";
  import { useDeviceProperties } from "../../sheet/v1/interaction/document.svelte";

  let Host = $host();

  const device = useDeviceProperties();

  let floater = $state<HTMLElement>();

  /*                                                                          */
  let focusTarget = $derived.by(useActiveElement());

  /*                                         */
  let floaterTarget = $derived.by(() => {
    if (!isActive(focusTarget)) return undefined; /*                                                                      */

    /*                                                             */
    if (document.body === focusTarget) return undefined;

    return getComputedStyle(focusTarget).getPropertyValue("--oc-floating-focus-v1-full-bleed")
      ? findNextPositionedParent(focusTarget)
      : focusTarget;
  });

  let isVisible: boolean = $state(false);

  /*                                                        */
  let floaterTargetIsMoving: boolean = $state(false);
  let floaterTargetRect: DOMRect | undefined = undefined;

  $effect(() => {
    track(floaterTarget);
    floaterTargetIsMoving = false;
    floaterTargetRect = undefined;
  });

  const handleFloaterState = (): void => {
    if (!floaterTarget) return;
    repositionElement(floater!, floaterTarget);

    /*                                    */
    const newRect = floaterTarget.getBoundingClientRect();
    floaterTargetIsMoving =
      !!floaterTargetRect &&
      !(
        newRect.x === floaterTargetRect.x &&
        newRect.y === floaterTargetRect.y &&
        newRect.width === floaterTargetRect.width &&
        newRect.height === floaterTargetRect.height
      );
    floaterTargetRect = newRect;

    if (
      document.body === Host.parentElement &&
      getComputedStyle(document.body).position === "static"
    ) {
      /*                                                                      */
      Host.style.left = `0`;
      Host.style.top = `0`;
    } else {
      /*                                                                              */
      /*                                                                            */
      Host.style.left = `${-(Host.parentElement?.getBoundingClientRect().x ?? 0)}px`;
      Host.style.top = `${-(Host.parentElement?.getBoundingClientRect().y ?? 0)}px`;
    }
  };

  $effect(() => {
    track(floaterTarget);
    handleFloaterState();
  });

  let borderRadius = $derived.by(() => {
    if (!focusTarget) return undefined;
    if (!floaterTarget) return undefined;

    const customBorderRadius = getComputedStyle(focusTarget).getPropertyValue(
      "--oc-floating-focus-v1-radius",
    );

    if (customBorderRadius) return customBorderRadius;

    let minRadius = getComputedStyle(Host).borderRadius;
    let targetBorderRadius = getComputedStyle(floaterTarget).borderRadius;

    return parseInt(targetBorderRadius, 10) < parseInt(minRadius, 10)
      ? undefined
      : targetBorderRadius;
  });

  let outlineOffset = $derived.by(() => {
    if (!focusTarget) return undefined;
    if (!floaterTarget) return undefined;
    return getComputedStyle(focusTarget).getPropertyValue("--oc-floating-focus-v1-offset") || "0px";
  });

  const handleMouseDown = (): void => {
    isVisible = false;
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) &&
      focusTarget?.classList.contains("card__interactive")
    )
      return;

    if (!["Tab", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
    isVisible = true;
  };

  let isScrolling = $state(false);
  let isScrollingInterval: NodeJS.Timeout;
  const handleScrollResize = (): void => {
    clearInterval(isScrollingInterval);
    isScrolling = true;
    handleFloaterState();
    isScrollingInterval = setTimeout(() => (isScrolling = false), 100);
  };

  onMount(() => on(document.body, "scroll", handleScrollResize, { passive: true, capture: true }));

  $effect(() => {
    if (!device.isTouchable && isVisible && floaterTarget) useInterval(handleFloaterState, 100);
  });

  /*                                                        */
  let noTransition = $state(false);
  $effect(() => {
    if (floaterTarget && isVisible) {
      requestAnimationFrame(() => {
        noTransition = false;
      });
    } else {
      noTransition = true;
    }
  });

  /*                                             */
  $effect(() => {
    if (!focusTarget) return;
    if (!floaterTarget) return;
    if (!isVisible) return;
    if (getComputedStyle(focusTarget).getPropertyValue("--oc-floating-focus-v1-preserve-outline"))
      return;

    const target = focusTarget;
    target.classList.add("oc-floating-focus-v1-hide-outline");
    return () => target.classList.remove("oc-floating-focus-v1-hide-outline");
  });

  $effect(() => {
    /*                                                                                     */
    if (!floaterTarget)
      Object.assign(floater!.style, {
        top: "0",
        left: "0",
        width: "0",
        height: "0",
      });
  });
</script>

<svelte:window
  onresizecapture={handleScrollResize}
  ontouchstart={handleMouseDown}
  onmousedown={handleMouseDown}
  onkeydown={handleKeyDown}
/>

<div
  bind:this={floater}
  class="floating-focus"
  class:floating-focus--visible={isVisible && floaterTarget}
  class:floating-focus--inactive={device.isTouchable}
  class:floating-focus--stopped={isScrolling || noTransition || floaterTargetIsMoving}
  style:--radius={borderRadius}
  style:--offset={outlineOffset}
></div>

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;

  :host {
    position: absolute;
    left: 0;
    top: 0;
    border-radius: #{tokens.$oc-semantic-focus-outline-radius-12}; /*                                           */
  }

  .floating-focus {
    border: 0 solid currentColor;
    border-radius: var(--radius, inherit);
    position: absolute;
    transform: translate(-50%, -50%);
    opacity: 0;
    will-change: top, left, width, height, border-width, border-radius;
    box-sizing: content-box;
    pointer-events: none;
    overflow: hidden;
    outline: tokens.$oc-semantic-focus-outline-radius-12 solid
      tokens.$oc-semantic-focus-outline-color;
    outline-offset: calc(var(--offset) + #{tokens.$oc-semantic-focus-outline-offset});

    @media (prefers-reduced-motion: no-preference) {
      transition-property: left, top, width, height, border-width, border-radius;
      transition-duration: 0.1s, 0.1s, 0.1s, 0.1s, 0.1s, 0.1s;
      transition-timing-function: ease, ease, ease, ease, ease, ease;
    }

    &--visible {
      opacity: 1;
    }

    &--stopped {
      transition: none;
    }
    /*                                                               */
    /*                                                  */
    &--inactive {
      display: none;
    }
  }
</style>
